import type {
  Confidentiality,
  Department,
  DocumentFileType,
  DocumentRecord,
  DocumentVersion,
  QRRecord,
} from '../../types';

export interface CreateDocumentInput {
  department: Department;
  existingDocuments: DocumentRecord[];
  userId: string;
  nameAr: string;
  nameEn?: string;
  bookNumber?: string;
  category: string;
  confidentiality: Confidentiality;
  fileType: DocumentFileType;
  fileName: string;
  mimeType: string;
  originalBytes: number;
  compressedBytes: number;
  pageCount: number;
  contentHash?: string;
  driveFileId?: string;
  driveConnectorId?: string;
  providerFolderId?: string;
  storagePolicyUsed?: DocumentRecord['storagePolicyUsed'];
  driveWebViewUrl?: string;
  previewUrl?: string;
  source?: DocumentRecord['source'];
  documentDate?: string;
  sender?: string;
  recipient?: string;
  retentionUntil?: string;
  notes?: string;
  tags?: string[];
  exceptionApproved?: boolean;
  exceptionReason?: string;
}

function randomToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return Array.from(bytes, b => b.toString(36).padStart(2, '0')).join('').slice(0, 30);
}

export function nextArchiveId(department: Department, documents: DocumentRecord[], year = new Date().getFullYear()): string {
  const prefix = `${department.code}-${year}-`;
  const max = documents
    .filter(document => document.archiveId.startsWith(prefix))
    .reduce((current, document) => {
      const suffix = Number(document.archiveId.slice(prefix.length));
      return Number.isFinite(suffix) ? Math.max(current, suffix) : current;
    }, 0);
  return `${prefix}${String(max + 1).padStart(6, '0')}`;
}

export function buildDocument(input: CreateDocumentInput): { document: DocumentRecord; qr: QRRecord } {
  const now = new Date().toISOString();
  const documentId = crypto.randomUUID();
  const qrId = crypto.randomUUID();
  const archiveId = nextArchiveId(input.department, input.existingDocuments);
  const token = randomToken();
  const version: DocumentVersion = {
    id: crypto.randomUUID(),
    documentId,
    versionNumber: 1,
    fileSizeBytes: input.originalBytes,
    compressedBytes: input.compressedBytes,
    uploadedBy: input.userId,
    uploadedAt: now,
    driveFileId: input.driveFileId,
    driveConnectorId: input.driveConnectorId,
    fileName: input.fileName,
    mimeType: input.mimeType,
    contentHash: input.contentHash,
    isActive: true,
    changeNote: 'Initial upload',
  };

  const document: DocumentRecord = {
    id: documentId,
    archiveId,
    bookNumber: input.bookNumber || undefined,
    nameAr: input.nameAr.trim(),
    nameEn: input.nameEn?.trim() || undefined,
    departmentId: input.department.id,
    departmentSlug: input.department.slug,
    category: input.category,
    confidentiality: input.confidentiality,
    status: 'active',
    fileType: input.fileType,
    fileSizeBytes: input.originalBytes,
    compressedBytes: input.compressedBytes,
    pageCount: input.pageCount,
    fileName: input.fileName,
    mimeType: input.mimeType,
    contentHash: input.contentHash,
    driveFileId: input.driveFileId,
    driveConnectorId: input.driveConnectorId,
    providerFolderId: input.providerFolderId,
    storagePolicyUsed: input.storagePolicyUsed,
    driveWebViewUrl: input.driveWebViewUrl,
    previewUrl: input.previewUrl,
    source: input.source ?? 'upload',
    documentDate: input.documentDate,
    sender: input.sender,
    recipient: input.recipient,
    retentionUntil: input.retentionUntil,
    uploadedBy: input.userId,
    uploadedAt: now,
    updatedAt: now,
    notes: input.notes?.trim() || undefined,
    tags: input.tags ?? [],
    versions: [version],
    qrId,
    exceptionApproved: input.exceptionApproved,
    exceptionReason: input.exceptionReason,
  };

  const qr: QRRecord = {
    id: qrId,
    token,
    documentId,
    departmentId: input.department.id,
    status: 'active',
    resolvedUrl: `/q/${token}`,
    createdAt: now,
    updatedAt: now,
    scanCount: 0,
  };

  return { document, qr };
}
